import React, { useRef, useEffect } from 'react';
import { X, Camera } from 'lucide-react';

type CameraType = 'outlet' | 'visiting-card' | 'wtf-sticker';

interface CameraModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCapture: () => void;
  stream: MediaStream | null;
  cameraType: CameraType;
}

const CAMERA_CONFIG = {
  outlet: {
    title: 'Take Outlet Photo',
    dataAttribute: 'camera-video'
  },
  'visiting-card': {
    title: 'Take Visiting Card Photo', 
    dataAttribute: 'visiting-card'
  },
  'wtf-sticker': {
    title: 'Take WTF Contact Sticker Photo',
    dataAttribute: 'wtf-sticker'
  }
};

export const CameraModal: React.FC<CameraModalProps> = ({
  isOpen,
  onClose,
  onCapture,
  stream,
  cameraType
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const config = CAMERA_CONFIG[cameraType];

  useEffect(() => {
    if (isOpen && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      // Ensure video starts playing
      videoRef.current.play().catch(error => {
        console.error('Video play failed:', error);
      });
    }
    
    // Cleanup when modal closes
    return () => {
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [isOpen, stream]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      <div className="flex justify-between items-center p-4 bg-black text-white">
        <h2 className="text-lg font-semibold">{config.title}</h2>
        <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-800 transition-colors">
          <X className="w-6 h-6" />
        </button>
      </div>
      
      <div className="flex-1 flex items-center justify-center">
        <video 
          ref={videoRef}
          data-attribute={config.dataAttribute}
          autoPlay 
          playsInline 
          className="w-full h-full object-cover" 
        />
      </div>
      
      <div className="p-4 bg-black flex justify-center">
        <button 
          onClick={onCapture} 
          className="w-16 h-16 bg-white rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
        >
          <Camera className="w-8 h-8 text-black" />
        </button>
      </div>
    </div>
  );
};

export default CameraModal;
